import CustomPaper from '../../../containers/Paper'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { useContext, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { StatisticsContext } from '../../../context/StatisticsContext'
import StatisticsService from '../../../services/StatisticsService'
import { formatDaysToYearsAndMonths } from '../../../helpers/utils'
import CustomIconButton from '../../../components/IconButton'
import DownloadIcon from '@mui/icons-material/Download'
import { PDFDownloadLink } from '@react-pdf/renderer'
import TendenciesNumericInfoPDF from './TendenciesNumericInfoPDF'
import { Pie } from 'react-chartjs-2'
import { statisticsColors } from '../StatisticsPage'

interface WorkPlaceCountType {
    employerCountry: string
    amount: number
}

export interface WorkingAbroadUserType {
    firstName: string
    lastName: string
    country: string
    phone: string
    email: string
    currentEmployer: string
}

interface FreelancerOrEntrepeneurCount {
    type: 'Emprendedor' | 'Freelancer' | 'Otros'
    amount: number
}

interface FreelancersOrEntrepeneursResponse {
    freelancerOrEntrepreneurGraduates: FreelancerOrEntrepeneurCount[]
    freelancerGraduates: WorkingAbroadUserType[]
    entrepreneurGraduates: WorkingAbroadUserType[]
}

const TendenciesNumericInfo = () => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [averageTime, setAverageTime] = useState<number>()
    const [placeInfo, setPlaceInfo] = useState<WorkPlaceCountType[]>()
    const [abroadusers, setAbroadusers] = useState<WorkingAbroadUserType[]>()
    const [freelancersOrEntrepeneurs, setFreelancersOrEntrepeneurs] =
        useState<FreelancersOrEntrepeneursResponse>()

    useEffect(() => {
        const service = new StatisticsService()
        service.getAverageTimeInCompany(carreer, startDate, endDate).then((response) => {
            if (response.status !== 200) setAverageTime(0)
            else setAverageTime(response.data)
        })
        service.getWorkPlacePercentage(carreer, startDate, endDate).then((response) => {
            if (response.status !== 200) {
                setPlaceInfo([])
                setAbroadusers([])
            } else {
                setPlaceInfo(response.data.workPlaceCount)
                setAbroadusers(response.data.workingAbroadUsers)
            }
        })
        service
            .getFreelancerOrEntrepreneurGraduates(carreer, startDate, endDate)
            .then((response) => {
                if (response.status !== 200)
                    setFreelancersOrEntrepeneurs({
                        freelancerOrEntrepreneurGraduates: [],
                        freelancerGraduates: [],
                        entrepreneurGraduates: [],
                    })
                else setFreelancersOrEntrepeneurs(response.data)
            })
    }, [carreer, startDate, endDate])

    const getPercentage = (value: number) => {
        const total = freelancersOrEntrepeneurs!.freelancerOrEntrepreneurGraduates
            .map((x) => x.amount)
            .reduce((partialSum, a) => partialSum + a, 0)
        return total !== 0 ? Number(((100 * value) / total).toFixed(2)) : 0
    }

    const getPlacePercentage = (place: string) => {
        const total = Number(placeInfo?.map((x) => x.amount).reduce((a, b) => a + b, 0))
        return total !== 0
            ? Number(
                  (
                      ((placeInfo?.find((info) => info.employerCountry === place)?.amount ?? 0) *
                          100) /
                      total
                  ).toFixed(2),
              )
            : 0
    }

    const getChartData = (type: 'place' | 'employer') => {
        return {
            labels:
                type === 'place'
                    ? placeInfo?.map((x) => x.employerCountry)
                    : freelancersOrEntrepeneurs?.freelancerOrEntrepreneurGraduates.map((x) =>
                          x.type !== 'Otros' ? x.type : 'Relación de dependencia',
                      ),
            datasets: [
                {
                    label: 'Cantidad',
                    data:
                        type === 'place'
                            ? placeInfo?.map((x) => x.amount)
                            : freelancersOrEntrepeneurs?.freelancerOrEntrepreneurGraduates.map(
                                  (x) => x.amount,
                              ),
                    backgroundColor:
                        type === 'place'
                            ? statisticsColors.background.slice(2, 4)
                            : [...statisticsColors.background.slice(0, 2), '#CEDBED'],
                    borderColor:
                        type === 'place'
                            ? statisticsColors.border.slice(2, 4)
                            : [...statisticsColors.border.slice(0, 2), '#CEDBED'],
                    borderWidth: 2,
                    pointRadius: 8,
                    pointHoverRadius: 12,
                    tension: 0.3,
                    fill: true,
                },
            ],
        }
    }

    const getOptions = (type: 'place' | 'employer') => ({
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, labels: { boxWidth: 13 } },
            tooltip: {
                callbacks: {
                    label(context: any) {
                        const label: string = context.dataset.label
                        const value = context.raw
                        const percentage =
                            type === 'place'
                                ? Number(
                                      (
                                          (100 * value) /
                                          placeInfo!.map((x) => x.amount).reduce((a, b) => a + b, 0)
                                      ).toFixed(2),
                                  )
                                : getPercentage(value)
                        return `${label}: ${value} (${percentage}%)`
                    },
                },
            },
        },
    })

    return (
        <CustomPaper className="h-full flex flex-col gap-1 text-primary">
            <div className="flex justify-center items-center gap-2 font-semibold text-xl md:text-2xl">
                Otros datos
                {abroadusers && freelancersOrEntrepeneurs && (
                    <PDFDownloadLink
                        document={
                            <TendenciesNumericInfoPDF
                                abroadUsers={abroadusers}
                                freelanceUsers={freelancersOrEntrepeneurs.freelancerGraduates}
                                entrepeneurUsers={freelancersOrEntrepeneurs.entrepreneurGraduates}
                            />
                        }
                        fileName="Reporte_Graduados_Particulares.pdf"
                    >
                        <CustomIconButton
                            icon={<DownloadIcon />}
                            className="-mt-[2px]"
                        />
                    </PDFDownloadLink>
                )}
            </div>
            {averageTime !== undefined && placeInfo && freelancersOrEntrepeneurs !== undefined ? (
                <div className="grid grid-cols-1 md:grid-cols-3 h-full w-full">
                    <div className="col-span-1 md:col-span-2 flex flex-col justify-around h-full gap-2">
                        <div className="flex flex-col gap-1 border-2 border-solid border-primary rounded-lg p-1">
                            <span className="text-secondary text-sm md:text-lg flex items-center gap-1">
                                <AccessTimeIcon />
                                Tiempo medio de permanencia en un empleo:
                            </span>
                            <span className="text-sm md:text-lg font-semibold">
                                {averageTime
                                    ? formatDaysToYearsAndMonths(averageTime)
                                    : 'No hay datos'}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1 border-2 border-solid border-primary rounded-lg p-1">
                            <span className="text-secondary text-sm md:text-lg flex items-center gap-1">
                                <PlaceOutlinedIcon />
                                Lugar de trabajo (Argentina o el exterior):
                            </span>
                            <span className="text-sm md:text-lg font-semibold">
                                Empleos en Argentina:{' '}
                                {placeInfo.find((info) => info.employerCountry === 'Argentina')
                                    ?.amount ?? 0}
                                {` (${getPlacePercentage('Argentina')}% del total)`}
                                <br />
                                Empleos en el exterior:{' '}
                                {placeInfo.find((info) => info.employerCountry === 'Exterior')
                                    ?.amount ?? 0}
                                {` (${getPlacePercentage('Exterior')}% del total)`}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1 border-2 border-solid border-primary rounded-lg p-1">
                            <span className="text-secondary text-sm md:text-lg flex items-center gap-1">
                                <LightbulbOutlinedIcon className="text-secondary" />
                                Graduados emprendedores o freelance:
                            </span>
                            <span className="text-sm md:text-lg font-semibold">
                                Graduados emprendedores:{' '}
                                {
                                    freelancersOrEntrepeneurs.freelancerOrEntrepreneurGraduates.find(
                                        (x) => x.type === 'Emprendedor',
                                    )!.amount
                                }
                                {` (${getPercentage(
                                    freelancersOrEntrepeneurs.freelancerOrEntrepreneurGraduates.find(
                                        (x) => x.type === 'Emprendedor',
                                    )!.amount,
                                )}% del total)`}
                                <br />
                                Graduados freelancers:{' '}
                                {
                                    freelancersOrEntrepeneurs.freelancerOrEntrepreneurGraduates.find(
                                        (x) => x.type === 'Freelancer',
                                    )!.amount
                                }
                                {` (${getPercentage(
                                    freelancersOrEntrepeneurs.freelancerOrEntrepreneurGraduates.find(
                                        (x) => x.type === 'Freelancer',
                                    )!.amount,
                                )}% del total)`}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-row md:flex-col md:items-center md:justify-evenly h-[150px] md:h-full">
                        <div className="w-1/2 md:w-3/5 h-full md:h-3/5">
                            <Pie
                                data={getChartData('place')}
                                options={getOptions('place')}
                            />
                        </div>
                        <div className="w-1/2 md:w-4/5 h-full md:h-3/5">
                            <Pie
                                data={getChartData('employer')}
                                options={getOptions('employer')}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="w-full h-full flex justify-center items-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
        </CustomPaper>
    )
}

export default TendenciesNumericInfo
